import React from 'react';
import { useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  SectionContent,
  StyledList,
  GoBackButton,
  SplitSection,
  ImageTextOverlay,
  PageHeader,
  ContactLink
} from '../../components';
import SEO from '../../components/SEO';
import { INVESTMENT_PROGRAMS_CONTENT } from '../../data';
import { seoData } from '../../data/seoData';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import mqcapitalImage02 from '../../assets/images/strategies/mqcapital-image-02.jpg';
import strandQLogo from '../../assets/icons/strand-q-logo.svg';
import merinLogo from '../../assets/icons/merin-logo.svg';
import dravonLogo from '../../assets/icons/dravon-logo.svg';
import globalMacroLogo from '../../assets/icons/global-macro-logo-blue.svg';

const InvestmentProgramsPage = ({ setRefs }) => {
  const navigate = useNavigate();
  const overviewRef = useRef(null);
  const flagshipOfferingRef = useRef(null);
  const merinOfferingRef = useRef(null);
  const dravonOfferingRef = useRef(null);
  const globalMacroOfferingRef = useRef(null);
  const programDetailsRef = useRef(null);
  const futureOfferingsRef = useRef(null);
  const trackRecordRef = useRef(null);
  const customSolutionsRef = useRef(null);

  const refs = useMemo(
    () => ({
      overview: overviewRef,
      flagshipOffering: flagshipOfferingRef,
      merinOffering: merinOfferingRef,
      dravonOffering: dravonOfferingRef,
      globalMacroOffering: globalMacroOfferingRef,
      programDetails: programDetailsRef,
      futureOfferings: futureOfferingsRef,
      trackRecord: trackRecordRef,
      customSolutions: customSolutionsRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  return (
    <>
      <SEO
        title={seoData.investmentPrograms.title}
        content={seoData.investmentPrograms.content}
        keywords={seoData.investmentPrograms.keywords}
        canonical={seoData.investmentPrograms.canonical}
        ogImage={seoData.investmentPrograms.ogImage}
        ogType={seoData.investmentPrograms.ogType}
      />
      {/* Back Button */}
      <GoBackButton
        backTo={ROUTES.STRATEGIES.ROOT}
        backText='Back to Strategies'
      />
      <PageHeader
        id='hero'
        title={INVESTMENT_PROGRAMS_CONTENT.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />
      <ImageTextOverlay
        imageSrc={mqcapitalImage02}
        text={INVESTMENT_PROGRAMS_CONTENT.INTRO[0]}
        height={{ xs: '220px', sm: '200px', md: '200px', lg: '200px' }} // Adjust as needed for your content!
        boxSx={{
          width: '100%',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          textAlign: 'center'
        }}
        textSx={{
          fontSize: { xs: '1.07rem', sm: '1.15rem', md: '1.3rem' },
          lineHeight: 1.6,
          color: THEME.COLORS.NEUTRAL_WHITE,
          px: { xs: 2, sm: 3, md: 5 },
          zIndex: 2,
          width: '100%',
          maxWidth: { xs: '100%', md: '80%' }
        }}
        containerSx={{
          position: 'relative',
          width: '100%'
        }}
        overlayColor='rgba(0,0,0,0.5)'
      />
      <SectionContent
        id='overview'
        ref={refs.overview}
        initialSpacing={false}
        text={[
          {
            text: INVESTMENT_PROGRAMS_CONTENT.INTRO[1],
            marginBottom: SECTION_SPACING,
            marginTop: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />{' '}
      {/* Our Flagship Offering Section */}
      <Box
        sx={{
          mb: ELEMENT_SPACING,
          cursor: 'pointer',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid transparent',
          '&:hover': {
            opacity: 0.9,
            transform: 'translateY(-4px)',
            transition: 'all 0.3s ease-in-out',
            border: `1px solid ${THEME.COLORS.PRIMARY_BLUE}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }
        }}
        onClick={() => navigate(ROUTES.STRATEGIES.STRAND_Q)}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: THEME.SPACING.SMALL
          }}
        >
          <img
            src={strandQLogo}
            alt='Strand Q Logo'
            style={{
              height: '60px',
              objectFit: 'contain'
            }}
          />
          <Typography
            sx={{
              fontSize: '0.9rem',
              color: THEME.COLORS.NEUTRAL_GRAY,
              fontStyle: 'italic',
              alignSelf: 'center'
            }}
          >
            Click to view details →
          </Typography>
        </Box>
        <Typography
          variant='h3'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
            fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
            color: THEME.COLORS.NEUTRAL_GRAY,
            textAlign: 'left',
            margin: 0
          }}
        >
          Flagship Systematic Strategy
        </Typography>
        <Typography
          sx={{
            mt: 2,
            fontSize: '1.15rem',
            color: THEME.COLORS.NEUTRAL_BLACK
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.PROGRAMS.STRAND_Q.DESCRIPTION}
        </Typography>
        <Typography
          sx={{
            fontSize: '1rem',
            color: THEME.COLORS.NEUTRAL_GRAY,
            mt: 1
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.PROGRAMS.STRAND_Q.LAUNCH_DATE}
        </Typography>
        <Typography
          sx={{
            fontSize: '1rem',
            color: THEME.COLORS.NEUTRAL_GRAY
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.PROGRAMS.STRAND_Q.INSTRUMENTS_TRADED}
        </Typography>
      </Box>
      <Box
        sx={{
          mb: ELEMENT_SPACING,
          cursor: 'pointer',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid transparent',
          '&:hover': {
            opacity: 0.9,
            transform: 'translateY(-4px)',
            transition: 'all 0.3s ease-in-out',
            border: `1px solid ${THEME.COLORS.PRIMARY_BLUE}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }
        }}
        onClick={() => navigate(ROUTES.STRATEGIES.MERIN)}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: THEME.SPACING.SMALL
          }}
        >
          <img
            src={merinLogo}
            alt='Merin Logo'
            style={{
              height: '60px',
              objectFit: 'contain'
            }}
          />
          <Typography
            sx={{
              fontSize: '0.9rem',
              color: THEME.COLORS.NEUTRAL_GRAY,
              fontStyle: 'italic',
              alignSelf: 'center'
            }}
          >
            Click to view details →
          </Typography>
        </Box>
        <Typography
          variant='h3'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
            fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
            color: THEME.COLORS.NEUTRAL_GRAY,
            textAlign: 'left',
            margin: 0
          }}
        >
          Incubation
        </Typography>
        <Typography
          sx={{
            mt: 2,
            fontSize: '1.15rem',
            color: THEME.COLORS.NEUTRAL_BLACK
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.PROGRAMS.MERIN.DESCRIPTION}
        </Typography>
        <Typography
          sx={{
            fontSize: '1rem',
            color: THEME.COLORS.NEUTRAL_GRAY,
            mt: 1
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.PROGRAMS.MERIN.LAUNCH_DATE}
        </Typography>
        <Typography
          sx={{
            fontSize: '1rem',
            color: THEME.COLORS.NEUTRAL_GRAY
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.PROGRAMS.MERIN.INSTRUMENTS_TRADED}
        </Typography>
      </Box>
      <Box
        sx={{
          mb: ELEMENT_SPACING,
          cursor: 'pointer',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid transparent',
          '&:hover': {
            opacity: 0.9,
            transform: 'translateY(-4px)',
            transition: 'all 0.3s ease-in-out',
            border: `1px solid ${THEME.COLORS.PRIMARY_BLUE}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }
        }}
        onClick={() => navigate(ROUTES.STRATEGIES.DRAVON)}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: THEME.SPACING.SMALL
          }}
        >
          <img
            src={dravonLogo}
            alt='Dravon Logo'
            style={{
              height: '60px',
              objectFit: 'contain'
            }}
          />
          <Typography
            sx={{
              fontSize: '0.9rem',
              color: THEME.COLORS.NEUTRAL_GRAY,
              fontStyle: 'italic',
              alignSelf: 'center'
            }}
          >
            Click to view details →
          </Typography>
        </Box>
        <Typography
          variant='h3'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
            fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
            color: THEME.COLORS.NEUTRAL_GRAY,
            textAlign: 'left',
            margin: 0
          }}
        >
          Incubation
        </Typography>
        <Typography
          sx={{
            mt: 2,
            fontSize: '1.15rem',
            color: THEME.COLORS.NEUTRAL_BLACK
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.PROGRAMS.DRAVON.DESCRIPTION}
        </Typography>
        <Typography
          sx={{
            fontSize: '1rem',
            color: THEME.COLORS.NEUTRAL_GRAY,
            mt: 1
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.PROGRAMS.DRAVON.LAUNCH_DATE}
        </Typography>
        <Typography
          sx={{
            fontSize: '1rem',
            color: THEME.COLORS.NEUTRAL_GRAY
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.PROGRAMS.DRAVON.INSTRUMENTS_TRADED}
        </Typography>
      </Box>
      {/* Global Macro Section */}
      <Box
        sx={{
          mb: ELEMENT_SPACING,
          cursor: 'pointer',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid transparent',
          '&:hover': {
            opacity: 0.9,
            transform: 'translateY(-4px)',
            transition: 'all 0.3s ease-in-out',
            border: `1px solid ${THEME.COLORS.PRIMARY_BLUE}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }
        }}
        onClick={() => navigate(ROUTES.STRATEGIES.GLOBAL_MACRO)}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: THEME.SPACING.SMALL
          }}
        >
          <img
            src={globalMacroLogo}
            alt='Global Macro Logo'
            style={{
              height: '60px',
              objectFit: 'contain'
            }}
          />
        </Box>
        <Typography
          variant='h3'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
            fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
            color: THEME.COLORS.NEUTRAL_GRAY,
            textAlign: 'left',
            margin: 0
          }}
        >
          In Preparation
        </Typography>
        <Typography
          sx={{
            mt: 2,
            fontSize: '1.15rem',
            color: THEME.COLORS.NEUTRAL_BLACK
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.PROGRAMS.GLOBAL_MACRO.DESCRIPTION}
        </Typography>
        <Typography
          sx={{
            fontSize: '1rem',
            color: THEME.COLORS.NEUTRAL_GRAY,
            mt: 1
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.PROGRAMS.GLOBAL_MACRO.LAUNCH_DATE}
        </Typography>
        <Typography
          sx={{
            fontSize: '1rem',
            color: THEME.COLORS.NEUTRAL_GRAY
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.PROGRAMS.GLOBAL_MACRO.INSTRUMENTS_TRADED}
        </Typography>
      </Box>
      {/* Program Details Section with Split Layout */}
      <SectionContent
        id='programDetails'
        ref={refs.programDetails}
        title='Program Details'
        titleVariant='TitleSmall' // Changed to TitleSmall
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[]}
      />
      {/* Split Section for two StyledLists */}
      <SplitSection
        leftContent={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start'
            }}
          >
            <StyledList
              variant='blue'
              items={INVESTMENT_PROGRAMS_CONTENT.PROGRAM_DETAILS_LEFT}
            />
          </Box>
        }
        rightContent={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start'
            }}
          >
            <StyledList
              variant='blue'
              items={INVESTMENT_PROGRAMS_CONTENT.PROGRAM_DETAILS_RIGHT}
            />
          </Box>
        }
        sx={{ mb: SECTION_SPACING }}
        equalHeight={true} // Ensure equal heights for both columns
      />
      <SectionContent
        id='trackRecord'
        ref={refs.trackRecord} // Make sure to add this ref to your refs object
        initialSpacing={false}
        linkMappings={[
          {
            word: 'View Reports',
            url: '/library#reports',
            color: 'var(--neutral-gray-light)',
            textDecoration: 'underlined',
            newTab: false
          }
        ]}
        text={[
          {
            text: 'Track Record: in Library/Reports – View Reports',
            padding: `${THEME.SPACING.LARGE} ${THEME.SPACING.XL}`,
            backgroundColor: THEME.COLORS.PRIMARY_BLUE,
            color: THEME.COLORS.NEUTRAL_WHITE,
            fontSize: '1.3rem',
            borderRadius: '4px',
            textAlign: 'center',
            marginBottom: SECTION_SPACING
          }
        ]}
      />
      {/* Future Offerings Section */}
      <SectionContent
        id='futureOfferings'
        ref={refs.futureOfferings}
        title={INVESTMENT_PROGRAMS_CONTENT.FUTURE_OFFERINGS.TITLE}
        titleVariant='TitleSmall' // Changed to TitleSmall
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: INVESTMENT_PROGRAMS_CONTENT.FUTURE_OFFERINGS.DESCRIPTION,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Custom Solutions Section */}
      <SectionContent
        id='customSolutions'
        ref={refs.customSolutions}
        title={INVESTMENT_PROGRAMS_CONTENT.CUSTOM_SOLUTIONS.TITLE}
        titleVariant='TitleSmall' // Changed to TitleSmall
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: INVESTMENT_PROGRAMS_CONTENT.CUSTOM_SOLUTIONS.TEXT,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          },
          {
            text: INVESTMENT_PROGRAMS_CONTENT.CUSTOM_SOLUTIONS.HIGHLIGHT,
            textAlign: 'center',
            padding: `${THEME.SPACING.LARGE} ${THEME.SPACING.XL}`,
            backgroundColor: THEME.COLORS.PRIMARY_BLUE,
            color: THEME.COLORS.NEUTRAL_WHITE,
            marginBottom: SECTION_SPACING,
            marginTop: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          marginTop: SECTION_SPACING, // Added spacing for consistency
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {INVESTMENT_PROGRAMS_CONTENT.FOOTER_NOTE}
        </Typography>
        <Box mt={ELEMENT_SPACING}>
          <Typography
            variant='body2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.2rem',
              color: THEME.COLORS.PRIMARY_BLUE,
              lineHeight: 1.6
            }}
          >
            Talk to an Expert –&nbsp;
            <ContactLink
              style={{
                color: THEME.COLORS.NEUTRAL_GRAY,
                textDecoration: 'underline',
                fontSize: '1rem'
              }}
            >
              Schedule Your meeting
            </ContactLink>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default InvestmentProgramsPage;
